<template>
  <v-container fluid>
    <div>
      <loading-overlay :visible="loading || isUploading" :label="isUploading ? $t('states.uploading') : $t('states.loading')" />

      <div class="gallery">

        <!-- Toolbar -->
        <div class="gallery-toolbar">
          <h2 class="gallery-title">{{ $t('app.gallery.title') }}</h2>

          <div class="toolbar-actions">
            <transition name="fade">
              <div v-if="deleting" class="bulk-actions">
                <button
                  class="btn btn--delete"
                  :disabled="selectedCount === 0"
                  @click="deleteSelectedImages"
                >
                  {{ $t('app.gallery.delete-selected', { count: selectedCount }) }}
                </button>
                <button class="btn btn--cancel" @click="toggleDeleteMode">
                  {{ $t('app.gallery.cancel-selection') }}
                </button>
              </div>
            </transition>

            <button
              v-if="$store.getters.user.role === 'p' && !deleting"
              class="btn btn--icon"
              :title="$t('app.gallery.delete-mode')"
              @click="toggleDeleteMode"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"></path>
                <path d="M10 11v6M14 11v6"></path>
                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"></path>
              </svg>
            </button>
          </div>
        </div>

        <transition name="fade">
          <p v-if="deleting" class="selection-hint">
            {{ selectedCount > 0
              ? $t('app.gallery.selected-count', { count: selectedCount })
              : $t('app.gallery.tap-to-select') }}
          </p>
        </transition>

        <p v-if="error" class="gallery-error">{{ error }}</p>

        <!-- Grid -->
        <div v-if="uploads && uploads.length > 0" class="photo-grid">
          <div
            v-for="upload in uploads"
            :key="upload.id"
            class="photo-cell"
            :class="{
              'photo-cell--selectable': deleting,
              'photo-cell--selected': isSelected(upload.id)
            }"
            @click="onCellClick(upload.id)"
          >
            <auto-rotate v-if="isImageUpload(upload)">
              <img
                :src="upload.blobUri"
                :alt="upload.originalName"
                class="photo-cell__img"
              />
            </auto-rotate>

            <video
              v-if="isVideoUpload(upload)"
              class="photo-cell__img photo-cell__video-preview"
              preload="metadata"
              muted
              playsinline
              tabindex="-1"
              aria-hidden="true"
            >
              <source :src="upload.blobUri" />
              <track kind="captions" srclang="en" label="Captions unavailable" src="data:text/vtt;charset=utf-8,WEBVTT" />
            </video>

            <div
              v-if="isVideoUpload(upload) && !deleting"
              class="photo-cell__play-indicator"
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"></path>
              </svg>
            </div>

            <transition name="check">
              <div v-if="deleting" class="photo-cell__overlay">
                <div class="photo-cell__check" :class="{ 'photo-cell__check--active': isSelected(upload.id) }">
                  <svg v-if="isSelected(upload.id)" viewBox="0 0 12 10" fill="none">
                    <polyline points="1,5 4.5,8.5 11,1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div v-else-if="!loading" class="gallery-empty">
          <p>{{ $t("app.gallery.not-found") }}</p>
        </div>

        <modal
          class="gallery-viewer-modal"
          :name="'gallery-media-viewer'"
          width="100%"
          height="100%"
          :adaptive="true"
          @closed="onMediaViewerClosed"
        >
          <div v-if="selectedViewerUpload" class="gallery-viewer">
            <button
              class="gallery-viewer__close"
              type="button"
              :title="$t('app.gallery.cancel-selection')"
              @click="closeMediaViewer"
            >
              &times;
            </button>

            <button
              v-if="canNavigateMedia"
              class="gallery-viewer__nav gallery-viewer__nav--prev"
              type="button"
              title="Previous"
              @click="showPreviousMedia"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <button
              v-if="canNavigateMedia"
              class="gallery-viewer__nav gallery-viewer__nav--next"
              type="button"
              title="Next"
              @click="showNextMedia"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>

            <div class="gallery-viewer__media" :key="`viewer-media-${selectedViewerUpload.id}`">
              <img
                v-if="isImageUpload(selectedViewerUpload)"
                :src="selectedViewerUpload.blobUri"
                :alt="selectedViewerUpload.originalName"
                class="gallery-viewer__image"
              />

              <video
                v-else-if="isVideoUpload(selectedViewerUpload)"
                ref="viewerVideo"
                :key="`viewer-video-${selectedViewerUpload.id}`"
                class="gallery-viewer__video"
                controls
                autoplay
                playsinline
              >
                <source :src="selectedViewerUpload.blobUri" :type="selectedViewerUpload.mimeType" />
                <track kind="captions" srclang="en" label="Captions unavailable" src="data:text/vtt;charset=utf-8,WEBVTT" />
              </video>
            </div>

            <p v-if="selectedViewerUpload.originalName" class="gallery-viewer__caption">
              {{ selectedViewerUpload.originalName }}
            </p>

            <p v-if="canNavigateMedia" class="gallery-viewer__counter">
              {{ selectedViewerIndex + 1 }} / {{ uploads.length }}
            </p>
          </div>
        </modal>
      </div>

      <!--  Upload FAB -->
      <div
        class="fab"
        v-if="$store.getters.user.role !== 'o'"
        @click="$refs.fileInput.$el.click()"
      >
        <Upload
          @fileSelect="showModal"
          ref="fileInput"
          accept="image/*, video/*"
          hidden
        />
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
      </div>

      <GalleryPrivacyModal @select="setAccess" />
    </div>
  </v-container>
</template>

<script>
import { neoparentUploads } from "@/api";

const MAX_IMAGE_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_FILE_SIZE_BYTES = 300 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;

export default {
  name: "neoparent-gallery",
  data() {
    return {
      uploads: [],
      deleting: false,
      access: "pub",
      file: null,
      isUploading: false,
      loading: true,
      selectedUploadIds: [],
      selectedViewerIndex: null,
      error: null
    };
  },
  computed: {
    selectedCount() {
      return this.selectedUploadIds.length;
    },
    selectedViewerUpload() {
      if (this.selectedViewerIndex === null) return null;
      return this.uploads[this.selectedViewerIndex] || null;
    },
    canNavigateMedia() {
      return this.uploads.length > 1;
    }
  },
  watch: {
    selectedViewerUpload(upload) {
      if (!upload || !this.isVideoUpload(upload)) return;

      this.$nextTick(() => {
        const viewerVideoRef = this.$refs.viewerVideo;
        const viewerVideo = Array.isArray(viewerVideoRef) ? viewerVideoRef[0] : viewerVideoRef;
        if (!viewerVideo) return;

        viewerVideo.load();

        const playPromise = viewerVideo.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      });
    }
  },
  mounted() {
    this.getUploads();
  },
  methods: {
    async getUploads() {
      try {
        const result = await neoparentUploads.getAllForPatient(
          this.$store.getters.user.patient.id
        );
        const uploads = result?.data?.uploads;
        this.uploads = Array.isArray(uploads) ? this.formatUploads(uploads) : [];
      } catch (err) {
        this.error = err.response?.data?.message || this.$t("error.general");
        this.consoleLog(err);
      } finally {
        this.loading = false;
      }
    },
    formatUploads(uploads) {
      return uploads.map(upload => ({
        ...upload,
        src: upload.blobUri,
        thumb: upload.blobUri,
        caption: `<h4>${upload.originalName || ""}</h4>`
      }));
    },
    isSelected(id) {
      return this.selectedUploadIds.includes(id);
    },
    isImageUpload(upload) {
      return Boolean(upload?.mimeType && upload.mimeType.toLowerCase().startsWith("image/"));
    },
    isVideoUpload(upload) {
      return Boolean(upload?.mimeType && upload.mimeType.toLowerCase().startsWith("video/"));
    },
    toggleDeleteMode() {
      this.deleting = !this.deleting;
      if (!this.deleting) this.selectedUploadIds = [];
    },
    toggleSelectedUpload(id) {
      if (!this.deleting) return;
      this.selectedUploadIds = this.isSelected(id)
        ? this.selectedUploadIds.filter(x => x !== id)
        : [...this.selectedUploadIds, id];
    },
    onCellClick(id) {
      if (this.deleting) {
        this.toggleSelectedUpload(id);
      } else {
        const clickedIndex = this.uploads.findIndex(upload => upload.id === id);
        if (clickedIndex < 0) return;
        this.openMediaViewer(clickedIndex);
      }
    },
    openMediaViewer(index) {
      this.pauseGridVideoPreviews();
      this.selectedViewerIndex = index;
      this.$modal.show("gallery-media-viewer");
    },
    closeMediaViewer() {
      this.pauseActiveViewerVideo();
      this.$modal.hide("gallery-media-viewer");
    },
    showPreviousMedia() {
      if (!this.canNavigateMedia || this.selectedViewerIndex === null) return;

      this.pauseActiveViewerVideo();
      this.selectedViewerIndex = (this.selectedViewerIndex - 1 + this.uploads.length) % this.uploads.length;
    },
    showNextMedia() {
      if (!this.canNavigateMedia || this.selectedViewerIndex === null) return;

      this.pauseActiveViewerVideo();
      this.selectedViewerIndex = (this.selectedViewerIndex + 1) % this.uploads.length;
    },
    pauseGridVideoPreviews() {
      if (!this.$el) return;

      const previews = this.$el.querySelectorAll(".photo-cell__video-preview");
      previews.forEach((preview) => {
        if (preview.paused) return;
        preview.pause();
        preview.currentTime = 0;
      });
    },
    pauseActiveViewerVideo() {
      const viewerVideo = this.$refs.viewerVideo;
      if (!viewerVideo || Array.isArray(viewerVideo) || viewerVideo.paused) return;

      viewerVideo.pause();
      viewerVideo.currentTime = 0;
    },
    onMediaViewerClosed() {
      this.pauseActiveViewerVideo();
      this.selectedViewerIndex = null;
    },
    async deleteSelectedImages() {
      if (this.$store.getters.user.role !== "p" || this.selectedCount === 0) return;
      if (!confirm(this.$t("app.gallery.delete-selected-confirm", { count: this.selectedCount }))) return;

      this.loading = true;
      this.error = null;
      const ids = [...this.selectedUploadIds];

      try {
        await Promise.all(ids.map(id => neoparentUploads.deleteImage(id)));
        await this.getUploads();
      } catch (err) {
        this.error = err.response?.data?.message || this.$t("error.general");
        this.consoleLog(err);
      } finally {
        this.selectedUploadIds = [];
        this.deleting = false;
        this.loading = false;
      }
    },
    showModal(files) {
      if (!files || !files.length) return;

      const file = files[0];

      if (!file.type || (!file.type.startsWith("image/") && !file.type.startsWith("video/"))) {
        this.error = "Only image and video files are allowed.";
        return;
      }

      const maxAllowedSize = file.type.startsWith("video/")
        ? MAX_VIDEO_FILE_SIZE_BYTES
        : MAX_IMAGE_FILE_SIZE_BYTES;

      if (file.size > maxAllowedSize) {
        const mediaTypeLabel = file.type.startsWith("video/") ? "video" : "image";
        this.error = `The selected ${mediaTypeLabel} is too large. Maximum allowed size is ${Math.ceil(maxAllowedSize / (1024 * 1024))} MB.`;
        return;
      }

      this.error = null;
      this.$modal.show("gallery-modal");
      this.file = file;
    },
    setAccess(access) {
      this.access = access;
      this.handleFile();
    },

    // Resize image to fit within MAX_IMAGE_DIMENSION on the longest edge.
    // Returns the file unchanged for videos, GIFs, or images already within the limit.
    async compressImage(file) {
      if (!file.type.startsWith("image/") || file.type === "image/gif") {
        return file;
      }

      return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);

          const { naturalWidth: w, naturalHeight: h } = img;

          if (Math.max(w, h) <= MAX_IMAGE_DIMENSION) {
            resolve(file);
            return;
          }

          const scale = MAX_IMAGE_DIMENSION / Math.max(w, h);
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);

          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              resolve(blob
                ? new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() })
                : file
              );
            },
            "image/jpeg",
            JPEG_QUALITY
          );
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
        };

        img.src = objectUrl;
      });
    },

    async handleFile() {
      if (this.isUploading || !this.file) return;
      this.isUploading = true;
      this.error = null;

      try {
        const processedFile = await this.compressImage(this.file);

        const formData = new FormData();
        formData.append("file", processedFile, processedFile.name);
        formData.append("access", this.access);
        formData.append("mimeType", processedFile.type);
        formData.append("patientId", this.$store.getters.patient.id);

        await neoparentUploads.uploadFile(formData);
        this.$modal.hide("gallery-modal");
        await this.getUploads();
      } catch (err) {
        this.$modal.hide("gallery-modal");
        this.error = err.response?.data?.message || this.$t("error.general");
        this.consoleLog(err);
      } finally {
        this.isUploading = false;
        this.file = null;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.gallery {
  padding: 16px 16px 80px;
}

.gallery-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  min-height: 44px;
}

.gallery-title {
  font-size: 18px;
  font-weight: 600;
  color: #00a9b3;
  margin: 0;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selection-hint {
  font-size: 13px;
  color: #8e8e8e;
  margin: 0 0 12px;
  text-align: right;
}

.gallery-error {
  color: #e05484;
  font-size: 14px;
  margin: 0 0 12px;
}

// Grid

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
}

// Cell

.photo-cell {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: #e8f5f6;
  cursor: pointer;
  isolation: isolate;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.02);
  }

  &--selected {
    box-shadow: inset 0 0 0 3px #e05484;
  }

  &--selectable:hover:not(.photo-cell--selected) {
    box-shadow: inset 0 0 0 3px rgba(224, 84, 132, 0.35);
  }
}

.photo-cell__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.photo-cell__video-preview {
  pointer-events: none;
}

.photo-cell__play-indicator {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 18px;
    height: 18px;
    margin-left: 2px;
    display: block;
  }
}

// Selection overlay

.photo-cell__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 8px;
}

.photo-cell__check {
  // Visual size 28px, but padding expands the touch area to 44px
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  box-sizing: content-box;
  padding: 8px;
  margin: -8px;

  &--active {
    background: #e05484;
    border-color: #e05484;
  }

  svg {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }
}

// Buttons

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 18px;
  border-radius: 22px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 2px solid transparent;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.8;
  }
}

.btn--delete {
  background: #fff;
  color: #e05484;
  border-color: #e05484;
}

.btn--cancel {
  background: #fff;
  color: #31b9be;
  border-color: #31b9be;
}

.btn--icon {
  background: transparent;
  border: none;
  min-height: 44px;
  min-width: 44px;
  padding: 0;
  color: #8e8e8e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    display: block;
  }

  &:hover {
    color: #e05484;
  }
}

.bulk-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.gallery-empty {
  padding: 40px 0;
  text-align: center;
  color: #8e8e8e;
}

.gallery-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 56px 16px 18px;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.gallery-viewer__media {
  width: 100%;
  max-width: 980px;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-viewer__image,
.gallery-viewer__video {
  width: 100%;
  max-width: 980px;
  max-height: calc(100vh - 190px);
  border-radius: 10px;
  background: #000;
  object-fit: contain;
}

.gallery-viewer__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  z-index: 30;
}

.gallery-viewer__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  background: rgba(20, 28, 36, 0.72);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 30;

  svg {
    width: 24px;
    height: 24px;
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }

  &:hover {
    background: rgba(20, 28, 36, 0.86);
  }
}

.gallery-viewer__nav--prev {
  left: 12px;
}

.gallery-viewer__nav--next {
  right: 12px;
}

@media (max-width: 960px) {
  .gallery-viewer__nav {
    width: 48px;
    height: 48px;
  }

  .gallery-viewer__nav--prev {
    left: 8px;
  }

  .gallery-viewer__nav--next {
    right: 8px;
  }
}

.gallery-viewer__caption {
  margin: 12px 0 0;
  color: #fff;
  font-size: 14px;
  text-align: center;
}

.gallery-viewer__counter {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
}

// FAB

.fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #00a9b3;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 169, 179, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  z-index: 10;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover {
    transform: scale(1.07);
    box-shadow: 0 6px 18px rgba(0, 169, 179, 0.5);
  }

  input {
    display: none;
  }
}

// Transitions

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.check-enter-active {
  transition: opacity 0.15s ease;
}
.check-enter {
  opacity: 0;
}
</style>